import {
  ReactElement,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
  useMemo
} from 'react';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';

type PopOverSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'screen-xl'
  | 'screen-2xl'
  | 'fullscreen';

interface PopOverProps {
  /** The trigger element that opens the popover */
  trigger: ReactElement;
  /** The content to be displayed in the popover */
  children: ReactNode;
  /** Size of the popover */
  size?: PopOverSize;
  /** Additional CSS classes */
  className?: string;
  /** Whether to close the popover when clicking outside */
  closeOnOutsideClick?: boolean;
  /** Control the visibility of the popover from parent */
  show?: boolean;
  /** Callback when popover is opened or closed */
  onStateChange?: (isOpen: boolean) => void;
  /** Position of the popover relative to the trigger */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Distance from the trigger element */
  offset?: number;
  /** Whether to close the popover when pressing Escape key */
  closeOnEsc?: boolean;
  /** Whether the popover should have a focus trap */
  trapFocus?: boolean;
}

const sizeClasses: Record<PopOverSize, string> = {
  'xs': 'max-w-xs',
  'sm': 'max-w-sm',
  'md': 'max-w-md',
  'lg': 'max-w-lg',
  'xl': 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  'screen-xl': 'max-w-screen-xl',
  'screen-2xl': 'max-w-screen-2xl',
  'fullscreen': 'h-screen w-screen max-w-screen',
};

const positionClasses = {
  top: 'bottom-full mb-2',
  bottom: 'mt-2',
  left: 'right-full mr-2',
  right: 'ml-2',
};

const PopOver = memo(function PopOver({
  trigger,
  children,
  size = '5xl',
  className = '',
  closeOnOutsideClick = true,
  closeOnEsc = true,
  show = false,
  onStateChange,
  position = 'bottom',
  offset = 8,
  trapFocus = true,
}: PopOverProps) {
  const [isOpen, setIsOpen] = useState(show);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Update internal state when show prop changes
  useEffect(() => {
    setIsOpen(show);
  }, [show]);

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.(isOpen);
  }, [isOpen, onStateChange]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle outside clicks
  useEffect(() => {
    if (!closeOnOutsideClick) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current && 
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, closeOnOutsideClick, handleClose]);

  // Handle Escape key
  useEffect(() => {
    if (!closeOnEsc || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEsc, handleClose]);

  // Calculate position classes
  const positionClass = useMemo(() => positionClasses[position], [position]);
  const sizeClass = useMemo(() => sizeClasses[size], [size]);

  // Memoize the panel content to prevent unnecessary re-renders
  const panelContent = useMemo(() => (
    <div 
      className={clsx(
        'absolute z-50 w-screen sm:w-auto',
        'bg-white',
        'rounded-lg shadow-lg ring-1 ring-black/5',
        'focus:outline-none',
        positionClass,
        sizeClass,
        className
      )}
      ref={popoverRef}
      style={{
        [position === 'top' || position === 'bottom' ? 'minWidth' : 'minHeight']: '500px',
      }}
    >
      <div className="overflow-y-auto max-h-[calc(100vh-2rem)]">
        {children}
      </div>
    </div>
  ), [positionClass, sizeClass, className, position, children]);

  return (
    <Popover as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <Popover.Button
            ref={triggerRef}
            onClick={handleToggle}
            className="focus:outline-none"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
          >
            {trigger}
          </Popover.Button>

          <Transition
            show={isOpen}
            as={Popover.Panel}
            static
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            className="fixed z-50"
            style={{
              [position === 'left' || position === 'right' ? 'top' : 'left']: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {panelContent}
          </Transition>
        </>
      )}
    </Popover>
  );
});

PopOver.displayName = 'PopOver';

export { PopOver };
export type { PopOverProps, PopOverSize };
